

import { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { formatCurrency, formatCurrencyFull, formatDate, localToday } from "@/lib/data";
import { useToast } from "@/context/ToastContext";
import { X, CreditCard, Calendar, Building2, CheckCircle2, AlertCircle, RefreshCw, ChevronDown, ChevronUp, Info, Layers } from "lucide-react";
import { eventsApi, mastersApi, apiClient } from "@/lib/api";
import ImageUpload from "@/components/ImageUpload";

interface VendorPayment {
  id: number;
  amount: number;
  payment_date: string;
  paid_at?: string;
  payment_method: string;
  transaction_id?: string;
  reference?: string;
  notes?: string;
  event_vendor_id: number;
}

interface VendorPaymentItem {
  type: 'vendor' | 'service';
  id: number;
  description: string;
  quantity: number;
  cost_price: number;
  total_amount: number;
}

interface VendorSummary {
  vendor_id: number;
  vendor_name: string;
  total_amount: number;
  paid_amount: number;
  balance: number;
  items: VendorPaymentItem[];
  payments: VendorPayment[];
}

interface VendorPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  onPaymentAdded: () => void;
  preselectedVendorId?: number;
  /** Financial vendor cost total (incl. commission) from event financials — overrides API sum */
  financialVendorCost?: number;
}

function MarginBadge({ value }: { value: number }) {
  const pos = value >= 0;
  return (
    <span className={`inline-block text-[9px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap ${pos ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
      {pos ? "+" : ""}{value}%
    </span>
  );
}

function TypeBadge({ type }: { type: "service" | "vendor" }) {
  if (type === "service") {
    return <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-wider">Service</span>;
  }
  return <span className="inline-block text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 uppercase tracking-wider">Vendor</span>;
}

const DEFAULT_PAYMENT_METHODS = [
  { value: "cash", label: "Cash", icon: "💵" },
  { value: "card", label: "Card", icon: "💳" },
  { value: "online", label: "Online", icon: "🌐" },
  { value: "bank_transfer", label: "Bank Transfer", icon: "🏦" },
  { value: "cheque", label: "Cheque", icon: "📄" },
];

export default function VendorPaymentModal({
  isOpen,
  onClose,
  eventId,
  onPaymentAdded,
  preselectedVendorId,
  financialVendorCost,
}: VendorPaymentModalProps) {
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  const [totals, setTotals] = useState({ total_to_pay: 0, total_paid: 0, total_balance: 0 });
  const [selectedVendor, setSelectedVendor] = useState<VendorSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [showBreakdown, setShowBreakdown] = useState(true);
  const [showItemAllocations, setShowItemAllocations] = useState(false);
  
  const [formData, setFormData] = useState({
    amount: "",
    payment_method: "",
    payment_date: localToday(),
    transaction_id: "",
    notes: "",
    proof_file: null as File | null,
  });

  useEffect(() => {
    mastersApi.paymentMethods.list({ status: 'active' })
      .then((res) => {
        const data = res.data?.data || res.data || [];
        if (data.length > 0) {
          const methods = data.map((m: any) => ({ value: m.name, label: m.name, icon: "" }));
          setPaymentMethods(methods);
          setFormData(prev => ({ ...prev, payment_method: methods[0].value }));
        }
      })
      .catch(() => {
        setFormData(prev => ({ ...prev, payment_method: DEFAULT_PAYMENT_METHODS[0].value }));
      });
  }, []);

  useEffect(() => {
    if (isOpen && eventId) {
      loadVendorSummary();
    }
  }, [isOpen, eventId]);

  // Handle preselected vendor
  useEffect(() => {
    if (preselectedVendorId && vendors.length > 0) {
      const vendor = vendors.find(v => v.vendor_id === preselectedVendorId);
      if (vendor && vendor.balance > 0) {
        setSelectedVendor(vendor);
        setFormData(prev => ({ ...prev, amount: vendor.balance.toString() }));
      }
    }
  }, [preselectedVendorId, vendors]);

  const loadVendorSummary = async () => {
    try {
      setError(null);
      const response = await eventsApi.getVendorPaymentSummary(eventId);
      // apiClient returns parsed JSON body directly: { success, data: { vendors, totals } }
      if (response.success) {
        const vendors = (response.data.vendors || [])
          .map((v: any) => ({
            vendor_id: v.vendor_id,
            vendor_name: v.vendor_name,
            total_amount: parseFloat(v.total_amount) || 0,
            paid_amount: parseFloat(v.paid_amount) || 0,
            balance: parseFloat(v.balance) || 0,
            items: (v.items || []).map((item: any) => ({
              type: item.type,
              id: item.id,
              description: item.description,
              quantity: item.quantity || 1,
              cost_price: parseFloat(item.cost_price) || 0,
              total_amount: parseFloat(item.total_amount) || 0,
            })),
            payments: (v.payments || []).map((p: any) => ({
              id: p.id,
              amount: parseFloat(p.amount) || 0,
              payment_date: p.payment_date || p.paid_at,
              payment_method: p.payment_method || '',
              transaction_id: p.transaction_id,
              notes: p.notes,
              event_vendor_id: p.event_vendor_id,
            })),
          }))
          // Filter out vendors with no items or payments
          .filter((v: any) => v.total_amount > 0 || v.payments.length > 0);

        setVendors(vendors);
        setTotals({
          total_to_pay: parseFloat(response.data.totals?.total_to_pay) || 0,
          total_paid: parseFloat(response.data.totals?.total_paid) || 0,
          total_balance: parseFloat(response.data.totals?.total_balance) || 0,
        });
      } else {
        setError(response.message || "Failed to load vendor payments");
        showToast("Failed to load vendor payments", "error");
      }
    } catch (error: any) {
      console.error("Error loading vendor summary:", error);
      setError("Failed to connect to server. Please try again.");
      showToast("Failed to load vendor payments", "error");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedVendor) {
      showToast("Please select a vendor", "error");
      return;
    }

    // Debug: Log form data values
    console.log("Form Data:", {
      amount: formData.amount,
      payment_method: formData.payment_method,
      payment_date: formData.payment_date,
      transaction_id: formData.transaction_id,
      proof_file: formData.proof_file?.name || null,
    });

    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      showToast("Please enter a valid amount", "error");
      return;
    }

    if (amount > selectedVendor.balance) {
      showToast(`Amount cannot exceed balance of ${formatCurrency(selectedVendor.balance)}`, "error");
      return;
    }

    // Ensure required fields are set
    const paymentMethod = formData.payment_method || paymentMethods[0]?.value || DEFAULT_PAYMENT_METHODS[0].value;
    const paymentDate = formData.payment_date || localToday();

    // Get the event_vendor_id from vendor-type items OR use vendor_id for service-assigned vendors
    const vendorItem = selectedVendor.items?.find(item => item.type === 'vendor');
    const eventVendorId = vendorItem?.id;
    const vendorId = selectedVendor.vendor_id; // Always have vendor_id for service-assigned vendors

    setLoading(true);
    try {
      // Build FormData for file upload support
      const formDataToSend = new FormData();
      
      if (eventVendorId) {
        formDataToSend.append('event_vendor_id', eventVendorId.toString());
      } else {
        // For service-assigned vendors, send vendor_id - backend will auto-create event_vendor
        formDataToSend.append('vendor_id', vendorId.toString());
      }
      
      formDataToSend.append('amount', amount.toString());
      formDataToSend.append('payment_method', paymentMethod);
      formDataToSend.append('payment_date', paymentDate);
      
      if (formData.transaction_id) {
        formDataToSend.append('transaction_id', formData.transaction_id);
      }
      if (formData.notes) {
        formDataToSend.append('notes', formData.notes);
      }
      if (formData.proof_file) {
        formDataToSend.append('proof_file', formData.proof_file);
      }

      // Debug: Log FormData entries
      console.log("FormData entries:");
      for (const [key, value] of formDataToSend.entries()) {
        console.log(`  ${key}:`, value instanceof File ? `File(${value.name})` : value);
      }

      // Use postFormData for multipart/form-data with file upload
      const response = await apiClient.postFormData(`/events/${eventId}/vendor-payments`, formDataToSend);

      // apiClient returns parsed JSON body directly: { success, data, message }
      if (response.success) {
        showToast(`Payment of ${formatCurrency(amount)} recorded successfully`, "success");
        setFormData(prev => ({
          amount: "",
          payment_method: prev.payment_method,
          payment_date: localToday(),
          transaction_id: "",
          notes: "",
          proof_file: null,
        }));
        setSelectedVendor(null);
        await loadVendorSummary();
        onPaymentAdded();
      } else {
        showToast(response.message || "Failed to add payment", "error");
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || "Failed to add payment";
      showToast(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const getPaymentDate = (payment: VendorPayment) => {
    return payment.payment_date || payment.paid_at || new Date().toISOString();
  };

  const getPaymentMethodLabel = (method: string) => {
    return paymentMethods.find(m => m.value === method)?.label || method;
  };

  const handleVendorSelect = (vendor: VendorSummary) => {
    setSelectedVendor(vendor);
    setFormData(prev => ({
      ...prev,
      amount: vendor.balance > 0 ? vendor.balance.toString() : "0",
      // Ensure payment_method has a value
      payment_method: prev.payment_method || paymentMethods[0]?.value || DEFAULT_PAYMENT_METHODS[0].value,
      // Ensure payment_date has a value
      payment_date: prev.payment_date || localToday(),
    }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Give Vendor Payment" size="xl">
      <div className="space-y-3">

        {/* Compact Summary Bar */}
        <div className="grid grid-cols-3 gap-2">
          <div className="bg-blue-50 rounded-lg px-3 py-2 border border-blue-100 flex items-center gap-2">
            <Building2 className="w-3.5 h-3.5 text-blue-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-blue-500 uppercase leading-none">Total to Pay</p>
              <p className="text-sm font-bold text-blue-700 mt-0.5">{formatCurrency(financialVendorCost ?? totals.total_to_pay)}</p>
            </div>
          </div>
          <div className="bg-emerald-50 rounded-lg px-3 py-2 border border-emerald-100 flex items-center gap-2">
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] font-medium text-emerald-500 uppercase leading-none">Total Paid</p>
              <p className="text-sm font-bold text-emerald-700 mt-0.5">{formatCurrency(totals.total_paid)}</p>
            </div>
          </div>
          <div className={`rounded-lg px-3 py-2 border flex items-center gap-2 ${totals.total_balance > 0 ? 'bg-amber-50 border-amber-100' : 'bg-emerald-50 border-emerald-100'}`}>
            <AlertCircle className={`w-3.5 h-3.5 shrink-0 ${totals.total_balance > 0 ? 'text-amber-500' : 'text-emerald-500'}`} />
            <div className="min-w-0">
              <p className={`text-[10px] font-medium uppercase leading-none ${totals.total_balance > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>Balance Due</p>
              <p className={`text-sm font-bold mt-0.5 ${totals.total_balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>
                {formatCurrency(financialVendorCost != null ? Math.max(0, financialVendorCost - totals.total_paid) : totals.total_balance)}
              </p>
            </div>
          </div>
        </div>

        {/* Vendor Breakdown Section */}
        {vendors.length > 0 && (
          <div className="border border-stone-200 rounded-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setShowBreakdown(!showBreakdown)}
              className="w-full flex items-center justify-between px-3 py-2 bg-stone-50 hover:bg-stone-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5 text-blue-500" />
                <span className="text-xs font-semibold text-stone-600 uppercase tracking-wide">
                  Vendor Breakdown
                </span>
              </div>
              {showBreakdown ? <ChevronUp className="w-3.5 h-3.5 text-stone-400" /> : <ChevronDown className="w-3.5 h-3.5 text-stone-400" />}
            </button>
            {showBreakdown && (
              <div className="px-3 pb-2 pt-1 bg-white">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-stone-100">
                      <th className="text-left py-1.5 text-stone-400 font-medium">Vendor</th>
                      <th className="text-right py-1.5 text-stone-400 font-medium">To Pay</th>
                      <th className="text-right py-1.5 text-stone-400 font-medium">Paid</th>
                      <th className="text-right py-1.5 text-stone-400 font-medium">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vendors.map((vendor) => (
                      <tr key={vendor.vendor_id} className="border-b border-stone-50 hover:bg-stone-50/60">
                        <td className="py-1.5 pr-2">
                          <span className="font-semibold text-stone-700">{vendor.vendor_name}</span>
                        </td>
                        <td className="py-1.5 text-right font-medium text-stone-700">{formatCurrency(vendor.total_amount)}</td>
                        <td className="py-1.5 text-right font-medium text-emerald-600">{formatCurrency(vendor.paid_amount)}</td>
                        <td className={`py-1.5 text-right font-semibold ${vendor.balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                          {formatCurrency(vendor.balance)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-stone-50 border-t border-stone-200">
                      <td className="py-1.5 font-bold text-stone-700">Total</td>
                      <td className="py-1.5 text-right font-bold text-blue-700">{formatCurrency(totals.total_to_pay)}</td>
                      <td className="py-1.5 text-right font-bold text-emerald-700">{formatCurrency(totals.total_paid)}</td>
                      <td className={`py-1.5 text-right font-bold ${totals.total_balance > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{formatCurrency(totals.total_balance)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}


        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-4 h-4 shrink-0" />
              <span className="text-xs font-medium">{error}</span>
            </div>
            <button onClick={loadVendorSummary} className="p-1 text-red-600 hover:bg-red-100 rounded" title="Retry">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Vendor Payment Cards - Consolidated by Vendor */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-xs font-semibold text-stone-600 uppercase tracking-wide flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" />
              Pay by Vendor
            </h3>
            <button onClick={loadVendorSummary} className="p-1 text-stone-400 hover:bg-stone-100 rounded" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
          </div>

          {!error && vendors.length === 0 ? (
            <div className="text-center py-6 bg-stone-50 rounded-xl border border-stone-200">
              <Building2 className="w-8 h-8 mx-auto text-stone-300 mb-1" />
              <p className="text-xs text-stone-400">No vendors assigned to this event</p>
            </div>
          ) : (
            <div className="space-y-2">
              {vendors.map((vendor) => (
                <div key={vendor.vendor_id} className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                  {/* Vendor Header - Compact */}
                  <div className="px-3 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-stone-800 text-sm">{vendor.vendor_name}</p>
                    </div>
                    <div className="flex items-center gap-4 ml-3 shrink-0 text-xs">
                      <div className="text-right">
                        <p className="text-stone-500 text-[10px]">To Pay</p>
                        <p className="font-bold text-stone-800">{formatCurrency(vendor.total_amount)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-stone-500 text-[10px]">Paid</p>
                        <p className="font-bold text-emerald-600">{formatCurrency(vendor.paid_amount)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-stone-500 text-[10px]">Balance</p>
                        <p className={`font-bold ${vendor.balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{formatCurrency(vendor.balance)}</p>
                      </div>
                    </div>
                  </div>

                  {/* Items Breakdown - Services & Vendor Assignments */}
                  {vendor.items && vendor.items.length > 0 && (
                    <div className="px-3 py-1.5 bg-stone-50/60 border-b border-stone-100">
                      <p className="text-[10px] font-semibold text-stone-400 uppercase mb-1">Services & Items ({vendor.items.length})</p>
                      <div className="space-y-0.5">
                        {vendor.items.map((item) => (
                          <div key={`${item.type}-${item.id}`} className="flex items-center justify-between text-xs py-0.5">
                            <div className="flex-1 min-w-0">
                              <span className={`inline-block text-[9px] font-bold px-1 py-0.5 rounded mr-1 ${item.type === 'service' ? 'bg-blue-100 text-blue-700' : 'bg-rose-100 text-rose-700'}`}>
                                {item.type === 'service' ? 'SVC' : 'VND'}
                              </span>
                              <span className="text-stone-600 truncate">{item.description}</span>
                              {item.quantity > 1 && <span className="text-stone-400 text-[9px] ml-1">x{item.quantity}</span>}
                            </div>
                            <span className="text-stone-700 font-semibold ml-2 shrink-0">{formatCurrency(item.total_amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment History (compact) */}
                  {vendor.payments && vendor.payments.length > 0 && (
                    <div className="px-3 py-1.5 bg-stone-50/60 border-b border-stone-100">
                      <p className="text-[10px] font-semibold text-stone-400 uppercase mb-1">Payments ({vendor.payments.length})</p>
                      <div className="space-y-0.5">
                        {vendor.payments.map((payment) => (
                          <div key={payment.id} className="flex items-center justify-between text-xs py-0.5">
                            <span className="text-stone-500">{formatDate(getPaymentDate(payment))}</span>
                            <span className="font-semibold text-emerald-600">{formatCurrencyFull(payment.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Payment Form or Button */}
                  {selectedVendor?.vendor_id === vendor.vendor_id ? (
                    <form onSubmit={handleSubmit} className="px-3 py-3 bg-blue-50/40 border-t border-blue-100 space-y-3">
                      {/* Header */}
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-semibold text-blue-700">Pay {vendor.vendor_name}</p>
                        <button type="button" onClick={() => setSelectedVendor(null)} className="p-1 text-blue-400 hover:bg-blue-100 rounded">
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Payment History for this vendor */}
                      {vendor.payments && vendor.payments.length > 0 && (
                        <div className="bg-white rounded-lg p-2 border border-stone-200">
                          <p className="text-[10px] font-semibold text-stone-500 uppercase mb-2">Payment History</p>
                          <div className="space-y-1 max-h-24 overflow-y-auto">
                            {vendor.payments.map((payment) => (
                              <div key={payment.id} className="flex items-center justify-between text-xs py-1 border-b border-stone-100 last:border-0">
                                <span className="text-stone-500">{formatDate(getPaymentDate(payment))}</span>
                                <span className="font-semibold text-emerald-600">{formatCurrencyFull(payment.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Amount - Pre-filled with balance */}
                      <div>
                        <label className="block text-[10px] font-medium text-stone-500 mb-1">Amount to Pay *</label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm">₹</span>
                          <input
                            type="number" step="0.01" min="0.01" max={vendor.balance}
                            value={formData.amount}
                            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                            className="w-full pl-8 pr-3 py-2.5 text-base font-semibold border border-stone-200 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="0.00" required
                          />
                        </div>
                        <p className="text-[10px] text-stone-400 mt-1">Balance: {formatCurrency(vendor.balance)}</p>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-medium text-stone-500 mb-1">Method *</label>
                          <select
                            value={formData.payment_method}
                            onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                            className="w-full px-2 py-2 text-sm border border-stone-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            required
                          >
                            {paymentMethods.map((method) => (
                              <option key={method.value} value={method.value}>{method.label}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-[10px] font-medium text-stone-500 mb-1">Date *</label>
                          <input
                            type="date" value={formData.payment_date}
                            onChange={(e) => setFormData({ ...formData, payment_date: e.target.value })}
                            className="w-full px-2 py-2 text-sm border border-stone-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-stone-500 mb-1">Transaction ID</label>
                        <input
                          type="text" value={formData.transaction_id}
                          onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                          className="w-full px-2 py-2 text-sm border border-stone-200 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Optional reference number"
                        />
                      </div>

                      {/* Proof Upload */}
                      <ImageUpload
                        selectedFile={formData.proof_file}
                        onFileSelect={(file) => setFormData({ ...formData, proof_file: file })}
                        maxSize={2 * 1024 * 1024}
                        compressToWebP={true}
                      />

                      <div className="flex flex-col sm:flex-row gap-2 pt-2">
                        <button
                          type="button" onClick={() => setSelectedVendor(null)}
                          className="flex-1 py-2.5 px-3 text-sm border border-stone-300 text-stone-600 rounded hover:bg-stone-100 transition-colors"
                        >Cancel</button>
                        <button
                          type="submit" disabled={loading}
                          className="flex-1 py-2.5 px-3 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded font-semibold disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                        >
                          {loading ? (
                            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <><CreditCard className="w-4 h-4" />Pay {formatCurrency(parseFloat(formData.amount) || 0)}</>
                          )}
                        </button>
                      </div>
                    </form>
                  ) : vendor.balance > 0 ? (
                    <div className="px-3 py-1.5 border-t border-stone-100">
                      <button
                        onClick={() => handleVendorSelect(vendor)}
                        className="w-full py-1.5 px-3 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors"
                      >
                        <CreditCard className="w-3.5 h-3.5" />
                        Pay {formatCurrency(vendor.balance)}
                      </button>
                    </div>
                  ) : (
                    <div className="px-3 py-1.5 border-t border-stone-100 flex items-center justify-center gap-1.5 text-emerald-600 text-xs font-medium">
                      <CheckCircle2 className="w-3.5 h-3.5" />
                      Paid
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
